import React, { useRef, useMemo, useState, useEffect } from "react";
import axios from "axios";
import { useParams, Navigate, Link } from "react-router-dom";
import { ArrowLeft, Phone, Star, Check, MapPin } from "lucide-react";
import { CITIES_BY_SLUG } from "@/lib/cities";
import { SITE, SERVICES, REVIEWS, GALLERY } from "@/lib/site";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CallFab } from "@/components/CallFab";
import { QuoteForm } from "@/components/QuoteForm";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { Toaster } from "sonner";
import { useSEO } from "@/lib/useSEO";
import { cityFAQs } from "@/lib/faqs";

export default function CityPage() {
  const { slug } = useParams();
  const city = CITIES_BY_SLUG[slug];
  const contactRef = useRef(null);
  const scrollToQuote = () =>
    contactRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  // Fetch live pricing for this city
  const [pricing, setPricing] = useState(null);
  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/pricing/${slug}`)
      .then((res) => { if (!cancelled) setPricing(res.data); })
      .catch(() => { if (!cancelled) setPricing(null); });
    return () => { cancelled = true; };
  }, [slug]);

  // Hooks must be called unconditionally; compute safe SEO values first
  const cityName = city?.name ?? "Ohio";
  const cityCounty = city?.county ?? "";
  const title = city
    ? `Pressure Washing in ${cityName}, OH | ClearView — 5.0 Stars`
    : "Pressure Washing in Ohio | ClearView";
  const description = city
    ? `Professional house washing, driveway cleaning, and gutter cleaning in ${cityName}, ${cityCounty} County, OH. 5-star rated. Free quotes. Call ${SITE.phone}.`
    : `ClearView Pressure Washing — serving Ohio.`;

  const jsonLd = useMemo(() => {
    if (!city) return null;
    const faqs = cityFAQs(cityName);
    return [
      {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        name: `ClearView Pressure Washing — ${cityName}`,
        image: "https://customer-assets.emergentagent.com/job_clearview-services/artifacts/aimy9eyc_IMG_4194.jpeg",
        telephone: SITE.phone,
        email: SITE.email,
        areaServed: { "@type": "City", name: `${cityName}, OH` },
        address: { "@type": "PostalAddress", addressRegion: "OH", addressCountry: "US", addressLocality: cityName },
        aggregateRating: { "@type": "AggregateRating", ratingValue: SITE.rating, reviewCount: SITE.reviewCount },
        priceRange: "$$",
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ];
  }, [city, cityName]);

  useSEO({ title, description, jsonLd });

  if (!city) return <Navigate to="/" replace />;

  // Pick reviews tagged with this city; fall back to top 3
  const localReviews = REVIEWS.filter((r) =>
    r.city.toLowerCase().includes(city.name.toLowerCase())
  );
  const showReviews = (localReviews.length ? localReviews : REVIEWS.slice(0, 3)).slice(0, 3);

  // Pick before/after pairs from gallery for the photo wall
  const wallPhotos = GALLERY.slice(0, 4);

  const faqs = cityFAQs(city.name);

  return (
    <div className="bg-white text-slate-900 antialiased">
      <Header onQuoteClick={scrollToQuote} />

      <main>
        {/* Hero */}
        <section className="pt-28 md:pt-36 pb-14 md:pb-20 relative overflow-hidden">
          <div
            aria-hidden
            className="absolute inset-0 -z-10 opacity-[0.5]"
            style={{
              backgroundImage:
                "radial-gradient(circle at 1px 1px, rgba(37,99,235,0.18) 1px, transparent 0)",
              backgroundSize: "28px 28px",
            }}
          />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link
              to="/#areas"
              data-testid="city-back-link"
              className="inline-flex items-center gap-1.5 text-sm font-bold text-blue-600 hover:text-blue-700"
            >
              <ArrowLeft className="h-4 w-4" />
              All service areas
            </Link>

            <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1.5 ring-1 ring-inset ring-blue-700/10">
              <MapPin className="h-3.5 w-3.5 text-blue-600" />
              <span className="text-xs font-bold text-blue-700">
                {city.county} County · Ohio
              </span>
            </div>

            <h1 className="font-display mt-5 text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] text-slate-900">
              Pressure washing in
              <span className="block text-blue-600">{city.name}, OH.</span>
            </h1>

            {pricing && (
              <div
                data-testid="city-pricing-badges"
                className="mt-6 flex flex-wrap gap-2"
              >
                <PriceBadge
                  label="House wash"
                  value={pricing.house_wash}
                  testid="price-house"
                />
                <PriceBadge
                  label="Driveway"
                  value={pricing.driveway}
                  testid="price-driveway"
                />
                <PriceBadge
                  label="Gutter clean"
                  value={pricing.gutter}
                  testid="price-gutter"
                />
              </div>
            )}

            <p className="mt-5 text-base md:text-lg text-slate-600 font-medium max-w-2xl leading-relaxed">
              {city.blurb}
            </p>

            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <Button
                data-testid="city-quote-btn"
                onClick={scrollToQuote}
                size="lg"
                className="bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl h-12 px-6 shadow-md"
              >
                Get a free quote in {city.name}
              </Button>
              <a
                href={SITE.phoneHref}
                data-testid="city-phone-btn"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-xl bg-white border-2 border-slate-200 text-slate-900 font-bold hover:border-blue-600 hover:text-blue-600 transition-all"
              >
                <Phone className="h-4 w-4" />
                {SITE.phone}
              </a>
            </div>

            <div className="mt-7 flex items-center gap-2">
              <div className="flex items-center gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <span className="text-xs font-bold text-slate-700">
                {SITE.rating.toFixed(1)} on Google · {SITE.reviewCountLabel} reviews
              </span>
            </div>
          </div>
        </section>

        {/* Services list */}
        <section className="py-16 md:py-20 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900 mb-8">
              What we do in {city.name}
            </h2>
            <div className="grid md:grid-cols-3 gap-5">
              {SERVICES.map((s) => (
                <article
                  key={s.id}
                  data-testid={`city-service-${s.id}`}
                  className="bg-white border border-slate-200 rounded-2xl overflow-hidden hover:shadow-md transition-shadow"
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={s.image}
                      alt={s.title}
                      className="h-full w-full object-cover"
                    />
                  </div>
                  <div className="p-5">
                    <h3 className="font-display font-extrabold text-slate-900 text-lg">
                      {s.title} in {city.name}
                    </h3>
                    <p className="mt-1.5 text-sm text-slate-600 font-medium leading-relaxed">
                      {s.short}
                    </p>
                    <ul className="mt-3 space-y-1.5">
                      {s.bullets.slice(0, 3).map((b) => (
                        <li
                          key={b}
                          className="flex items-center gap-2 text-xs text-slate-700 font-semibold"
                        >
                          <Check className="h-3.5 w-3.5 text-blue-600" strokeWidth={3} />
                          {b}
                        </li>
                      ))}
                    </ul>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Local reviews */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900 mb-8">
              {localReviews.length
                ? `What ${city.name} neighbors say`
                : `Customers love us across Ohio`}
            </h2>
            <div className="grid md:grid-cols-3 gap-5">
              {showReviews.map((r) => (
                <div
                  key={r.name + r.city}
                  data-testid="city-review"
                  className="bg-slate-50 rounded-2xl p-6 border border-slate-200"
                >
                  <div className="flex items-center gap-0.5 mb-3">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-sm text-slate-700 font-medium leading-relaxed">
                    "{r.text}"
                  </p>
                  <p className="mt-3 text-xs font-bold text-slate-900">
                    — {r.name}
                    <span className="text-slate-500 font-semibold"> · {r.city}</span>
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Recent jobs photo wall */}
        <section className="py-16 md:py-20 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
              <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900">
                Recent jobs near {city.name}
              </h2>
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                Real homes · Real results
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
              {wallPhotos.map((p, i) => (
                <div
                  key={i}
                  data-testid={`city-wall-${i}`}
                  className="relative overflow-hidden rounded-2xl ring-1 ring-slate-200 aspect-square group"
                >
                  <img
                    src={p.src}
                    alt={`${city.name} pressure washing — ${p.label}`}
                    className="absolute inset-0 h-full w-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <span
                    className={`absolute top-3 left-3 px-2.5 py-1 rounded-full text-[10px] font-black tracking-widest uppercase ${
                      p.label === "After" || p.label === "Finished"
                        ? "bg-blue-600 text-white"
                        : "bg-white/95 text-slate-700"
                    }`}
                  >
                    {p.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section
          id="faq"
          data-testid="city-faq-section"
          className="py-16 md:py-24 bg-white"
        >
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-blue-600">
              Frequently asked
            </p>
            <h2 className="font-display mt-3 text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900">
              {city.name} pressure washing — answered.
            </h2>

            <Accordion
              type="single"
              collapsible
              className="mt-8 divide-y divide-slate-200 border-t border-b border-slate-200"
            >
              {faqs.map((f, i) => (
                <AccordionItem
                  key={f.q}
                  value={`faq-${i}`}
                  data-testid={`faq-item-${i}`}
                  className="border-0"
                >
                  <AccordionTrigger className="py-5 text-left font-display font-bold text-slate-900 text-base md:text-lg hover:no-underline hover:text-blue-600">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 font-medium leading-relaxed text-sm md:text-base pb-5">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            <div className="mt-10 text-center">
              <p className="text-sm font-medium text-slate-600 mb-3">
                Still have a question? We're a phone call away.
              </p>
              <a
                href={SITE.phoneHref}
                data-testid="faq-cta-phone"
                className="inline-flex items-center gap-2 px-6 h-12 rounded-xl bg-slate-900 text-white font-display font-extrabold hover:bg-blue-600 transition-colors"
              >
                <Phone className="h-4 w-4" />
                {SITE.phone}
              </a>
            </div>
          </div>
        </section>

        <QuoteForm ref={contactRef} />
      </main>

      <Footer />
      <CallFab />
      <Toaster richColors position="top-center" />
    </div>
  );
}

const PriceBadge = ({ label, value, testid }) => (
  <span
    data-testid={testid}
    className="inline-flex items-baseline gap-1.5 rounded-full bg-slate-900 text-white px-3.5 py-1.5"
  >
    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-300">
      {label}
    </span>
    <span className="font-display font-extrabold text-base">
      from ${value}
    </span>
  </span>
);
